import type { App } from "vue";
import AppTable from "@/components/AppTable.vue";
import AppSearch from "@/components/AppSearch.vue";
import AppBadge from "@/components/AppBadge.vue";
import AppService from "@/services/AppService";
import AppModal from "@/components/AppModal.vue";
import ApiClient from "@/apis/ApiClient";
import type { Store } from "vuex";
import { createVfm } from "vue-final-modal";
import { useModal, useVfm } from "vue-final-modal";
import ConfirmModal from "@/modals/ConfirmModal.vue";
import AppFormInput from "@/components/AppFormInput.vue";
import AppFormTextarea from "@/components/AppFormTextarea.vue";
import CrossValidationObserver from "@/components/CrossValidationObserver.vue";
import CrossValidationProvider from "@/components/CrossValidationProvider.vue";
import CrossValidation from "@/validators/cross/cross-validation";
import AppFormInputNumber from "@/components/AppFormInputNumber.vue";
import AppMultiSelect from "@/components/AppMultiSelect.vue";
import { PermissionDirective } from "@/directives/permission.directive";
import moment from "moment";
import AppDraggableTable from "@/components/AppDraggableTable.vue";
import AppStepper from "@/components/AppStepper.vue";
import ManualButton from "@/components/ManualButton.vue";
import FileUpload from "@/components/FileUpload.vue";
import ProvisioningStatus from "@/components/ProvisioningStatus.vue";
import ProvisioningQosStatus from "@/components/ProvisioningQosStatus.vue";
import AppSelect from "@/components/AppSelect.vue";
import AppSelectSearch from "@/components/AppSelectSearch.vue";
import AppLabelGroup from "@/components/AppLabelGroup.vue";

// モーダルのzIndexを取得
// @see https://vue-final-modal.org/api/components/vue-final-modal#zindexfn
function zIndexFn(context: { index: number }) {
  // VuetifyのAppBarは1004なので、modalは1004より大きいに指定
  return 1004 + 2 * context.index;
}

export default {
  install: (app: App<Element>, options: { store: Store<any> }) => {
    // create vue-final-modal plugin and register it
    const vfm = createVfm();
    app.use(vfm);

    app.component("AppTable", AppTable);
    app.component("AppSearch", AppSearch);
    app.component("AppModal", AppModal);
    app.component("AppBadge", AppBadge);
    app.component("AppFormInput", AppFormInput);
    app.component("AppFormTextarea", AppFormTextarea);
    app.component("AppSelect", AppSelect);
    app.component("AppSelectSearch", AppSelectSearch);
    app.component("CrossValidationObserver", CrossValidationObserver);
    app.component("CrossValidationProvider", CrossValidationProvider);
    app.component("AppFormInputNumber", AppFormInputNumber);
    app.component("AppMultiSelect", AppMultiSelect);
    app.component("AppDraggableTable", AppDraggableTable);
    app.component("AppStepper", AppStepper);
    app.component("ManualButton", ManualButton);
    app.component("FileUpload", FileUpload);
    app.component("ProvisioningStatus", ProvisioningStatus);
    app.component("ProvisioningQosStatus", ProvisioningQosStatus);
    app.component("AppLabelGroup", AppLabelGroup);
    app.directive("permission", PermissionDirective);

    // MomentのInstance
    moment.defaultFormat = "YYYY/MM/DD HH:mm";
    app.config.globalProperties.$moment = moment;
    // サービス（業務ロジック）
    app.config.globalProperties.$service = new AppService(options.store);
    app.config.globalProperties.$api = new ApiClient(options.store);

    /**
     * 確認モーダル表示メソッド
     * @param message モーダルに表示するメッセージ
     * @param title モーダルのタイトル
     * @param isClose true:閉じるボタンのみ表示 false: はい、いいえ二つボタンを表示、デフォルトはfalse
     */
    app.config.globalProperties.$confirm = (
      message: string,
      title?: string,
      isClose?: boolean
    ): Promise<any> => {
      return new Promise((resolve) => {
        const { open, close } = useModal({
          keepAlive: false,
          component: ConfirmModal as any,
          attrs: {
            ...{ message, title, isClose },
            zIndexFn,
            onCancel() {
              close();
            },
            onOk() {
              close();
              resolve("OK");
            },
          },
        });
        return open();
      });
    };

    // モーダル（汎用）
    app.config.globalProperties.$modal = {
      /**
       * 汎用モーダル表示メソッド
       * @param component モーダル
       * @param bind モーダルへ渡すProperty
       */
      show: (component: any, bind?: any): Promise<any> => {
        return new Promise((resolve) => {
          const { open, close } = useModal({
            keepAlive: false,
            component: component,
            attrs: {
              ...bind,
              zIndexFn,
              onCancel() {
                close();
              },
              onOk(param: unknown) {
                close();
                resolve(param);
              },
            },
          });
          return open();
        });
      },
    };
    // this.$vfm.closeAll()が使えるため
    app.config.globalProperties.$vfm = useVfm();
    // 相関チェック用クラス
    app.config.globalProperties.$crossValidation = new CrossValidation();
  },
};
